import React from 'react';
import cn from 'classnames';
import { useFormikContext, getIn } from 'formik';

import { Typography } from 'components/shared/Typography';
import { Button } from 'components/shared/Button';

import { getTranslation } from 'helpers/getTranslation';

import { TheoryBlocks } from 'components/TheoryBlocks';
import { FormikField } from 'components/FormikField';
import { EditableTextField } from 'components/FormikFields';
import { TaskPreviewImage } from 'components/TaskPreviewImage';

import styles from './GeneralSection.module.scss';

const GeneralSection = ({
  namePrefix,
  taskIndex,
  designType,
  translateLoading,
  activeLocale,
  onTranslateClick,
}: {
  namePrefix: any;
  taskIndex: any;
  designType: any;
  translateLoading?: boolean;
  activeLocale: string;
  onTranslateClick: () => void;
}) => {
  const { values } = useFormikContext<any>();

  const task = getIn(values, namePrefix);

  if (!task) {
    return null;
  }

  const isInitialState = namePrefix === 'initialState';
  const hasPreview = designType === 'DESIGN' && !isInitialState;

  const currentTextValue = getIn(values, `${namePrefix}.text`);

  return (
    <div className={styles.section}>
      {designType === 'DESIGN' && !isInitialState && (
        <div className={styles.preview}>
          <TaskPreviewImage
            preview={task.preview}
            className={styles.previewImgWrapper}
            imgClassName={styles.previewImg}
          />
        </div>
      )}
      {isInitialState ? (
        <div className={styles.info}>
          <Typography className={styles.infoTitle} variant="subtitle">
            {getTranslation(
              'figma_lesson_page.main_settings.general_section.initial_state',
            )}
          </Typography>
          <div className={cn(styles.preview, styles.initialPreview)}>
            <TaskPreviewImage
              preview={task.preview}
              className={styles.previewImgWrapper}
              imgClassName={styles.previewImg}
            />
          </div>
          <Typography className={styles.infoText} variant="body1">
            {getTranslation(
              'figma_lesson_page.main_settings.general_section.initial_text',
            )}
          </Typography>
        </div>
      ) : (
        <div className={cn(styles.inner, !hasPreview && styles.innerWithoutPreview)}>
          <div className={styles.content}>
            <Typography className={styles.title} variant="subtitle">
              {getTranslation(
                'figma_lesson_page.main_settings.general_section.task',
              )}{' '}
              {taskIndex + 1}
            </Typography>
            {typeof currentTextValue === 'string' ? (
              <FormikField
                name={`${namePrefix}.text`}
                withControl={false}
                component={EditableTextField}
                componentProps={{
                  readOnly: values.readOnly,
                  className: styles.text,
                  variant: 'text',
                  multiline: true,
                  placeholder: getTranslation(
                    'figma_lesson_page.main_settings.general_section_placeholder',
                  ),
                }}
              />
            ) : (
              <TheoryBlocks namePrefix={`${namePrefix}.text`} />
            )}
          </div>

          <div className={styles.actions}>
            <Button
              className={styles.action}
              variant="outlined"
              size="sm"
              disabled={translateLoading || values.readOnly}
              onClick={onTranslateClick}
            >
              {getTranslation(
                activeLocale === 'ru'
                  ? 'figma_lesson_page.main_settings.general_section.translate_ru'
                  : 'figma_lesson_page.main_settings.general_section.translate_en',
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(GeneralSection) as any;
